import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import type { ConsentEvent } from '@/types/candidates';
import { consentEventTimeUtc } from '../lib/consent-expiry';
import {
  CONSENT_EVENT_LABEL,
  CONSENT_SOURCE_LABEL,
  consentEventTone,
} from '../lib/consent-labels';

interface CandidateGdprTimelineProps {
  events: ConsentEvent[];
}

export function CandidateGdprTimeline({ events }: CandidateGdprTimelineProps) {
  if (events.length === 0) {
    return (
      <section className="rounded-md border border-dashed border-border bg-muted/30 p-4 text-center text-sm text-muted-foreground">
        No consent events yet.
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-border bg-background">
      <header className="border-b border-border px-4 py-3">
        <h3 className="text-sm font-medium">Consent timeline</h3>
      </header>
      <ul className="divide-y divide-border">
        {events.map((e) => {
          const actorName = e.actor
            ? `${e.actor.firstName} ${e.actor.lastName}`.trim() || 'Unknown'
            : null;
          // HW-1057: a grant or a withdrawal from the email link has no actor —
          // the candidate acted. (A request with no actor stays "System".)
          const fallbackActor =
            e.source === 'EMAIL_LINK' && e.eventType !== 'REQUESTED' ? 'Candidate' : 'System';
          return (
            <li key={e.id} className="flex items-start gap-3 px-4 py-3">
              <span
                className={cn(
                  'shrink-0 rounded-md px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide',
                  consentEventTone(e.eventType),
                )}
              >
                {CONSENT_EVENT_LABEL[e.eventType]}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-foreground">
                  via <span className="font-medium">{CONSENT_SOURCE_LABEL[e.source]}</span>
                  <span className="text-muted-foreground"> · policy {e.policyVersion}</span>
                  {e.ipAddress ? (
                    <span className="text-muted-foreground"> · {e.ipAddress}</span>
                  ) : null}
                </div>
                {e.note ? (
                  <div className="mt-1 text-xs text-muted-foreground">“{e.note}”</div>
                ) : null}
                <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                  {e.actor ? (
                    <Avatar className="h-4 w-4 rounded-full">
                      {e.actor.avatarUrl ? (
                        <AvatarImage src={e.actor.avatarUrl} alt={actorName ?? ''} />
                      ) : null}
                      <AvatarFallback className="rounded-full text-[9px]">
                        {`${e.actor.firstName[0] ?? ''}${e.actor.lastName[0] ?? ''}`.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  ) : null}
                  <span>{actorName ?? fallbackActor}</span>
                  {/* HW-1023: in UTC, marked so — the day the consent's year is
                      counted from, as "Granted on" and the end date show it. */}
                  <span>· {consentEventTimeUtc(e.createdAt)}</span>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
